import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import DomainRoundedIcon from '@mui/icons-material/DomainRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import FolderCopyRoundedIcon from '@mui/icons-material/FolderCopyRounded'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import MapRoundedIcon from '@mui/icons-material/MapRounded'
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import SettingsInputAntennaRoundedIcon from '@mui/icons-material/SettingsInputAntennaRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded'
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded'
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'
import TopicRoundedIcon from '@mui/icons-material/TopicRounded'
import type { SvgIconComponent } from '@mui/icons-material'

import type { Permission } from '@/features/auth/permissions'
import { Permission as Perm } from '@/features/auth/permissions'
import type { ModuleCode } from '@/features/entitlements/types'
import { ModuleCode as Mod } from '@/features/entitlements/types'
import { paths } from '@/routes/paths'

export interface NavItem {
  label: string
  path: string
  icon: SvgIconComponent
  /** Si se omite, el ítem es visible para cualquier rol autenticado. */
  permission?: Permission
  /** SA-8 (Sección 16) — si se omite, el módulo comercial no aplica a
   * este ítem (p. ej. Administración es RBAC puro, no un módulo). */
  module?: ModuleCode
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * Transformación Comercial de UI: navegación agrupada por intención, no por
 * módulo técnico — responde en el orden en que un directivo pregunta "¿qué
 * está pasando, dónde, qué cambió, qué requiere atención, qué evidencia hay
 * detrás?" en vez de listar funciones del sistema una tras otra.
 */
export const navGroups: NavGroup[] = [
  {
    label: 'Inteligencia',
    items: [
      { label: 'Centro de Inteligencia', path: paths.dashboard, icon: SpaceDashboardRoundedIcon, module: Mod.CENTER_OF_INTELLIGENCE },
      { label: 'Pulso del Municipio', path: paths.pulse, icon: MonitorHeartRoundedIcon, module: Mod.CENTER_OF_INTELLIGENCE },
      { label: 'Inteligencia Territorial', path: paths.territorial, icon: MapRoundedIcon, module: Mod.TERRITORIAL },
      { label: 'Señales y Alertas', path: paths.signals, icon: NotificationsActiveRoundedIcon, module: Mod.SIGNALS },
      { label: 'Temas Estratégicos', path: paths.topics, icon: TopicRoundedIcon, module: Mod.CENTER_OF_INTELLIGENCE },
      { label: 'Evolución', path: paths.evolution, icon: TimelineRoundedIcon, permission: Perm.REPORTS_READ, module: Mod.EVOLUTION },
    ],
  },
  {
    label: 'Trabajo',
    items: [
      { label: 'GOVIA AI', path: paths.assistant, icon: SmartToyRoundedIcon, permission: Perm.AI_QUERY, module: Mod.GOVIA_AI },
      { label: 'Evidencia', path: paths.documents, icon: FolderCopyRoundedIcon, permission: Perm.DOCUMENTS_READ, module: Mod.EVIDENCE },
      { label: 'Reportes Ejecutivos', path: paths.reports, icon: SummarizeRoundedIcon, permission: Perm.REPORTS_READ, module: Mod.REPORTS },
    ],
  },
  {
    label: 'Administración',
    items: [
      { label: 'Dependencias', path: paths.dependencies, icon: DomainRoundedIcon, permission: Perm.COMPANIES_READ },
      // PC-5 -- SOURCES_MANAGE, no SOURCES_READ: esta pantalla incluye
      // alta/edición/ejecución real de fuentes y, para SUPER_ADMIN, el
      // catálogo de conectores y credenciales de webhook (ver
      // SourceHubPage.tsx) -- territorio de gestión, no de solo lectura.
      // Un VIEWER real ya tiene un destino propio y correcto para "ver mis
      // fuentes": /v2/monitoring (UX-2.5).
      { label: 'Centro de Fuentes', path: paths.sourceHub, icon: SettingsInputAntennaRoundedIcon, permission: Perm.SOURCES_MANAGE },
      { label: 'Organización', path: paths.organization, icon: BusinessRoundedIcon },
      { label: 'Usuarios', path: paths.users, icon: GroupRoundedIcon, permission: Perm.USERS_READ },
      { label: 'Auditoría', path: paths.audit, icon: FactCheckRoundedIcon, permission: Perm.AUDIT_READ },
      { label: 'Configuración', path: paths.settings, icon: SettingsRoundedIcon },
    ],
  },
]

/** Lista plana — para código que necesita buscar/recorrer todos los ítems (p. ej. el título del Topbar). */
export const navItems: NavItem[] = navGroups.flatMap((group) => group.items)
